import { useState, useEffect } from 'react';
import { Card, Descriptions, Text, Title } from '@bancobarigui/barihaus';
import { message } from 'antd';
import { InspectionRequest } from '@/services/types/inspection.types';
import { AddAttachmentModal } from '../AddAttachmentModal';
import { useDocumentDownload } from '@/hooks/useDocumentDownload';
import { inspectionService, UnifiedHistoryItem } from '@/services/inspection.service';
import { StatusCard } from './StatusCard';
import { ProponentsSection } from './ProponentsSection';
import { ItemsSection } from './ItemsSection';
import { AttachmentsSection } from './AttachmentsSection';
import { HistoryTimeline } from './HistoryTimeline';

interface InspectionInfoProps {
  inspection: InspectionRequest;
  onUpdate?: () => void;
}

export function InspectionInfo({ inspection, onUpdate }: InspectionInfoProps) {
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const { downloadDocument, loading: downloadingFile } = useDocumentDownload();
  const [historyItems, setHistoryItems] = useState<UnifiedHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const firstItem = inspection.items?.[0];
  const itemStatus = firstItem?.status;
  const itemStatusDescription = firstItem?.statusDescription || 'Sem status';

  useEffect(() => {
    const fetchHistory = async () => {
      if (!firstItem?.id) return;

      setLoadingHistory(true);
      try {
        const response = await inspectionService.getUnifiedHistory(firstItem.id);
        setHistoryItems(response.items || []);
      } catch {
        setHistoryItems([]);
      } finally {
        setLoadingHistory(false);
      }
    };

    fetchHistory();
  }, [firstItem?.id]);

  const handleDownloadAttachment = async (attachment: any) => {
    if (attachment.filePath && attachment.filePath.startsWith('docman/')) {
      try {
        await downloadDocument(attachment.filePath, attachment.fileName);
      } catch {
        message.error('Erro ao baixar arquivo do S3. Verifique se o serviço Docman está configurado.');
      }
    } else {
      message.warning({
        content: (
          <div>
            <div><strong>Anexo antigo</strong></div>
            <div>Este anexo foi criado antes da integração com S3.</div>
            <div>Apenas anexos novos podem ser baixados via S3.</div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
              FilePath: {attachment.filePath || 'não informado'}
            </div>
          </div>
        ),
        duration: 5,
      });
    }
  };

  const handleAttachmentSuccess = () => {
    if (onUpdate) {
      onUpdate();
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  return (
    <div className="space-y-6">
      <StatusCard status={itemStatus} statusDescription={itemStatusDescription} />

      <Card className="shadow-sm">
        <Title level={4} className="mb-4">
          Informações Gerais
        </Title>
        <Descriptions
          column={{ xs: 1, sm: 2 }}
          bordered
          size="middle"
          items={[
            {
              key: 'proposalNumber',
              label: <Text strong>Nº de Proposta</Text>,
              children: inspection.proposalNumber || '-',
            },
            {
              key: 'squad',
              label: <Text strong>Squad</Text>,
              children: inspection.squad || '-',
            },
            {
              key: 'channel',
              label: <Text strong>Canal</Text>,
              children: inspection.channel || '-',
            },
            {
              key: 'createdAt',
              label: <Text strong>Data de Criação</Text>,
              children: formatDate(inspection.createdAt),
            },
          ]}
        />
      </Card>

      <ProponentsSection proponents={inspection.proponents || []} />

      <ItemsSection items={inspection.items || []} />

      <AttachmentsSection
        attachments={inspection.attachments || []}
        onAddAttachment={() => setIsAttachmentModalOpen(true)}
        onDownloadAttachment={handleDownloadAttachment}
        downloadingFile={downloadingFile}
      />

      {firstItem && firstItem.id && (
        <AddAttachmentModal
          open={isAttachmentModalOpen}
          itemId={firstItem.id}
          onClose={() => setIsAttachmentModalOpen(false)}
          onSuccess={handleAttachmentSuccess}
        />
      )}

      <HistoryTimeline
        historyItems={historyItems}
        loading={loadingHistory}
        createdAt={inspection.createdAt}
        fallbackWorkflow={firstItem?.workflow}
      />
    </div>
  );
}
